import type { Thought } from './thought';

/**
 * The thoughts wall, packed: each card goes into the column that is shortest
 * so far, so cards of different heights sit without holes. Heights are
 * estimated from the text rather than measured — cards arrive as they are
 * read, and a layout that waited on measuring would move under the reader.
 * A card, once placed, never moves: what is appended only lengthens the tail.
 */
export function packColumns<T>(items: readonly T[], columns: number, heightOf: (item: T) => number): T[][] {
  const out: T[][] = Array.from({ length: Math.max(1, columns) }, () => []);
  const heights = out.map(() => 0);
  for (const item of items) {
    let shortest = 0;
    for (let c = 1; c < heights.length; c++) if (heights[c]! < heights[shortest]!) shortest = c;
    out[shortest]!.push(item);
    heights[shortest]! += heightOf(item);
  }
  return out;
}

/** A card's height, roughly, in pixels at the wall's usual width. */
export function estimateHeight(thought: Thought | undefined): number {
  // The padding, the player's row and the foot's, and the gaps between them.
  const HEAD = 14 + 34 + 10 + 10 + 32 + 8;
  if (!thought) return HEAD + 40;
  const lines = (text: string, perLine: number) => Math.max(1, Math.ceil(text.length / perLine));
  const title = thought.title ? lines(thought.title, 34) * 24 + 9 : 0;
  const words = thought.face.length > 0 ? thought.face : thought.standIn ? [thought.standIn] : [];
  return HEAD + title + words.reduce((sum, line) => sum + lines(line, 42) * 24 + 9, 0);
}
